import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, CalendarCheck } from 'lucide-react';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { useAttendanceRollQuery, useSaveAttendance } from './useAttendanceRoll';
import type { AppLayoutContext } from '~/types';

type Status = 'PRESENT' | 'ABSENT' | 'TARDY' | 'EXCUSED';
type Row = {
  personId: string;
  firstName: string;
  lastName: string;
  status: Status | null;
  notes: string;
  dirty: boolean;
};
type AttendanceRowSource = {
  status: Status;
  notes?: string | null;
  person: { id: string };
};

const UNRECORDED = '__unrecorded__';

function localDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    .format(new Date(year, month - 1, day));
}

function initialDateForYear(value: string, startsOn?: string, endsOn?: string) {
  if (!startsOn || !endsOn) return value;
  return value < startsOn || value > endsOn ? startsOn : value;
}

function rowsFor(
  studentPeople: readonly { id: string; firstName: string; lastName: string }[] = [],
  attendanceRecords: readonly AttendanceRowSource[] = [],
) {
  const recorded = new Map(attendanceRecords.map((record) => [record.person.id, record]));
  return [...studentPeople]
    .sort((first, second) =>
      first.lastName.localeCompare(second.lastName) || first.firstName.localeCompare(second.firstName))
    .map((person) => {
      const record = recorded.get(person.id);
      return {
        personId: person.id,
        firstName: person.firstName,
        lastName: person.lastName,
        status: record?.status ?? null,
        notes: record?.notes ?? '',
        dirty: false,
      } satisfies Row;
    });
}

export default function AttendanceRoll() {
  const { scheduleBlockId, yearSlug } = useParams();
  const navigate = useNavigate();
  const context = useOutletContext<AppLayoutContext | undefined>();
  const [searchParams, setSearchParams] = useSearchParams();
  const schoolYear = context?.school?.schoolYears?.find((year) => year.slug === yearSlug);
  const schoolYearStart = schoolYear?.startDate ? String(schoolYear.startDate) : undefined;
  const schoolYearEnd = schoolYear?.endDate ? String(schoolYear.endDate) : undefined;
  const requestedDate = searchParams.get('date') || localDateString();
  const date = initialDateForYear(requestedDate, schoolYearStart, schoolYearEnd);
  const [rows, setRows] = useState<Row[]>([]);
  const [saving, setSaving] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const { data, loading, error, refetch } = useAttendanceRollQuery(scheduleBlockId, date);
  const [saveAttendance] = useSaveAttendance();
  const block = data?.scheduleBlock;
  const actionKey = `${scheduleBlockId ?? ''}:${date}`;
  const currentActionKey = useRef(actionKey);
  const hydratedActionKey = useRef<string | null>(null);
  currentActionKey.current = actionKey;

  const canonicalRows = useMemo(
    () => rowsFor(block?.studentPeopleOn, block?.attendanceOn as readonly AttendanceRowSource[] | undefined),
    [actionKey, block?.attendanceOn, block?.studentPeopleOn],
  );

  useEffect(() => {
    const keyChanged = hydratedActionKey.current !== actionKey;
    hydratedActionKey.current = actionKey;
    setRows((current) => keyChanged || current.every((row) => !row.dirty) ? canonicalRows : current);
  }, [actionKey, canonicalRows]);

  useEffect(() => setRefreshMessage(null), [date, scheduleBlockId]);

  useEffect(() => {
    setSaving(false);
    setDiscarding(false);
    setErrorMessage(null);
  }, [actionKey]);

  useEffect(() => {
    if (searchParams.get('date') !== date) setSearchParams({ date }, { replace: true });
  }, [date, searchParams, setSearchParams]);

  const updateRow = (personId: string, patch: Partial<Pick<Row, 'status' | 'notes'>>) => {
    setRows((current) => current.map((row) => row.personId === personId ? { ...row, ...patch, dirty: true } : row));
  };
  const markAll = (status: Status) => {
    setRows((current) => current.map((row) => ({ ...row, status, dirty: true })));
  };
  const changedRows = rows.filter((row) => row.dirty);
  const busy = saving || discarding;

  const save = async () => {
    if (!scheduleBlockId || changedRows.length === 0 || busy) return;
    const startedFor = actionKey;
    const stillCurrent = () => currentActionKey.current === startedFor;
    setSaving(true);
    setErrorMessage(null);
    setRefreshMessage(null);
    let result;
    try {
      result = await saveAttendance({
        variables: {
          input: {
            scheduleBlockId,
            date,
            records: changedRows.map((row) => ({
              personId: row.personId,
              status: row.status,
              notes: row.notes.trim() || null,
            })),
          },
        },
      });
    } catch (caught) {
      if (stillCurrent()) {
        setErrorMessage(caught instanceof Error ? caught.message : 'Unable to save attendance.');
        setSaving(false);
      }
      return;
    }

    if (!stillCurrent()) return;

    const errors = result.data?.saveAttendance?.userErrors ?? [];
    if (errors.length > 0) {
      setErrorMessage(errors.map((item) => item.message).join(', '));
      setSaving(false);
      return;
    }

    setRows((current) => current.map((row) => row.dirty ? { ...row, dirty: false } : row));
    try {
      const refreshed = await refetch();
      if (stillCurrent() && refreshed.data?.scheduleBlock) {
        setRows(rowsFor(
          refreshed.data.scheduleBlock.studentPeopleOn,
          refreshed.data.scheduleBlock.attendanceOn as readonly AttendanceRowSource[],
        ));
      }
    } catch (caught) {
      if (!stillCurrent()) return;
      const detail = caught instanceof Error && caught.message ? `: ${caught.message}` : '';
      setRefreshMessage(`Attendance was saved, but the roll could not be refreshed${detail}. Reload the page to see the saved records.`);
    } finally {
      if (stillCurrent()) setSaving(false);
    }
  };

  const discard = async () => {
    if (busy) return;
    const startedFor = actionKey;
    const stillCurrent = () => currentActionKey.current === startedFor;
    setDiscarding(true);
    setErrorMessage(null);
    setRefreshMessage(null);
    try {
      const refreshed = await refetch();
      if (stillCurrent()) {
        const refreshedBlock = refreshed.data?.scheduleBlock;
        setRows(refreshedBlock ? rowsFor(
          refreshedBlock.studentPeopleOn,
          refreshedBlock.attendanceOn as readonly AttendanceRowSource[],
        ) : canonicalRows);
      }
    } catch (caught) {
      if (!stillCurrent()) return;
      const detail = caught instanceof Error && caught.message ? `: ${caught.message}` : '';
      setErrorMessage(`Unable to discard changes${detail}. Your unsaved edits are still shown.`);
    } finally {
      if (stillCurrent()) setDiscarding(false);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /></Button>
        <div><h1 className="text-3xl font-bold">Attendance</h1><p className="text-muted-foreground">Record the daily roll for one scheduled block occurrence.</p></div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div><CardTitle className="flex items-center gap-2"><CalendarCheck className="h-5 w-5" /> {block?.displayTitle ?? 'Schedule block'}</CardTitle><CardDescription>{block ? `${block.timeLabel}${block.classroom ? ` · ${block.classroom.name}` : ''}` : 'Loading schedule block…'}</CardDescription></div>
            <div className="space-y-2"><Label htmlFor="attendance-date">Date</Label><Input id="attendance-date" type="date" min={schoolYearStart} max={schoolYearEnd} value={date} disabled={busy} onChange={(event) => setSearchParams({ date: initialDateForYear(event.target.value, schoolYearStart, schoolYearEnd) })} /></div>
          </div>
        </CardHeader>
        <CardContent>
          {refreshMessage && <p role="alert" className="mb-4 text-sm text-destructive">{refreshMessage}</p>}
          {errorMessage && <p role="alert" className="mb-4 text-sm text-destructive">{errorMessage}</p>}
          {loading && !block ? <p className="text-sm text-muted-foreground">Loading attendance roll…</p> : error ? <p role="alert" className="text-sm text-destructive">Unable to load attendance: {error.message}</p> : !block ? <p className="text-sm text-muted-foreground">Schedule block not found.</p> : !block.attendanceEnabled ? <p className="text-sm text-muted-foreground">Attendance is not enabled for this schedule block.</p> : !block.occursOn ? <p className="text-sm text-muted-foreground">{block.displayTitle} does not occur on {formatDate(date)}. Choose one of its scheduled dates.</p> : rows.length === 0 ? <p className="text-sm text-muted-foreground">No students are in this block's audience on this date.</p> : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2"><span className="text-xs text-muted-foreground">Mark all:</span>{(['PRESENT', 'ABSENT', 'TARDY', 'EXCUSED'] as const).map((status) => <Button key={status} size="sm" variant="outline" disabled={busy} onClick={() => markAll(status)}>{status.charAt(0) + status.slice(1).toLowerCase()}</Button>)}</div>
              <div className="divide-y rounded-md border">
                {rows.map((row) => <div key={row.personId} className="grid gap-3 p-3 md:grid-cols-[minmax(10rem,1fr)_11rem_minmax(12rem,1.5fr)] md:items-center"><p className="font-medium">{row.lastName}, {row.firstName}</p><Select disabled={busy} value={row.status ?? UNRECORDED} onValueChange={(value) => updateRow(row.personId, { status: value === UNRECORDED ? null : value as Status })}><SelectTrigger aria-label={`Status for ${row.firstName} ${row.lastName}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value={UNRECORDED}>Unrecorded</SelectItem><SelectItem value="PRESENT">Present</SelectItem><SelectItem value="ABSENT">Absent</SelectItem><SelectItem value="TARDY">Tardy</SelectItem><SelectItem value="EXCUSED">Excused</SelectItem></SelectContent></Select><Input aria-label={`Notes for ${row.firstName} ${row.lastName}`} value={row.notes} disabled={busy || row.status === null} onChange={(event) => updateRow(row.personId, { notes: event.target.value })} placeholder="Optional notes" /></div>)}
              </div>
              {changedRows.length > 0 && <div className="flex justify-end gap-2"><Button variant="ghost" disabled={busy} onClick={() => void discard()}>{discarding ? 'Discarding…' : 'Discard changes'}</Button><Button disabled={busy} onClick={() => void save()}>{saving ? 'Saving…' : `Save ${changedRows.length} change${changedRows.length === 1 ? '' : 's'}`}</Button></div>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
